import React, { useEffect, useState } from "react";
import { getAllVideos, getVideoById } from "../helper/apicalls/videos";
import GlobalLoader from "../components/GlobalLoader";
import "../styles/Videos.scss";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CloseIcon from "@mui/icons-material/Close";
import notAvailable from "../assets/images/notAvalaible.svg";
import CommonPagination from "../components/common/CommonPagination";

const Videos = () => {
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalVideos, setTotalVideos] = useState(0);
  const limit = 10;

  useEffect(() => {
    fetchVideos(page);
  }, [page]);

  const fetchVideos = async (pageNumber) => {
    try {
      setLoading(true);
      const data = await getAllVideos(pageNumber, limit);
      if (data && Array.isArray(data.data)) {
        setVideos(data.data);
        const calculatedTotal = data.total_pages || (data.total && data.limit ? Math.ceil(data.total / data.limit) : 1);
        setTotalPages(calculatedTotal);
        setTotalVideos(data.total || data.data.length);
      } else if (Array.isArray(data)) {
        setVideos(data);
        setTotalPages(1);
        setTotalVideos(data.length);
      } else {
        console.warn("Unexpected video data structure:", data);
        setVideos([]);
        setTotalPages(1);
        setTotalVideos(0);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load videos.");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleVideoClick = async (id) => {
    try {
      setLoadingVideo(true);
      const data = await getVideoById(id);

      let videoData = data;
      if (data && data.data && !data.id) {
        videoData = data.data;
      }

      setSelectedVideo(videoData);
    } catch (err) {
      console.error(err);
      alert("Failed to load video details.");
    } finally {
      setLoadingVideo(false);
    }
  };

  const handleBack = () => {
    setSelectedVideo(null);
  };

  if (loading) return <GlobalLoader />;
  if (error)
    return (
      <div className="videos-page">
        <div className="not-found-container">
          <h3>{error}</h3>
        </div>
      </div>
    );

  const renderVideoPlayer = (video) => {
    const url = video.url || video.video_url || video.link;

    if (!url) return <p>Video URL not available.</p>;

    const isYoutube = url.includes("youtube.com") || url.includes("youtu.be");

    if (isYoutube) {
      let embedUrl = url;
      if (url.includes("watch?v=")) {
        embedUrl = url.replace("watch?v=", "embed/");
      } else if (url.includes("youtu.be/")) {
        embedUrl = url.replace("youtu.be/", "youtube.com/embed/");
      }
      return (
        <iframe
          src={embedUrl}
          title={video.title}
          allowFullScreen
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        />
      );
    }

    return (
      <video controls autoPlay>
        <source src={url} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    );
  };

  return (
    <div className="videos-page">
      {loadingVideo && <GlobalLoader />}

      <div className="videos-scroll-container custom-scrollbar">
        <div className="videos-grid">
          {videos.length === 0 ? (
            <div className="not-found-container">
              <img
                src={notAvailable}
                alt="PowerPoint Icon"
                style={{ width: "300px", height: "300px", marginTop: "4px" }}
              />
              <h3>No Videos Found!</h3>
            </div>
          ) : (
            videos.map((video) => (
              <div
                key={video.id || video._id}
                className="video-card"
                onClick={() => handleVideoClick(video.id || video._id)}
              >
                <div className="thumbnail-container">
                  {video.thumbnail_url || video.thumbnail ? (
                    <>
                      <img
                        src={video.thumbnail_url || video.thumbnail}
                        alt={video.title}
                        onError={(e) => {
                          e.target.style.display = "none";
                          const sibling =
                            e.target.parentElement.querySelector(
                              ".no-thumbnail-text",
                            );
                          if (sibling) sibling.style.display = "flex";
                        }}
                      />
                      <div
                        className="no-thumbnail-text"
                        style={{ display: "none" }}
                      >
                        No Thumbnail
                      </div>
                    </>
                  ) : (
                    <div className="no-thumbnail-text">No Thumbnail</div>
                  )}

                  <div className="play-icon">
                    <PlayArrowIcon style={{ color: "white", fontSize: 32 }} />
                  </div>
                  {video.duration && (
                    <span className="duration-badge">{video.duration}</span>
                  )}
                </div>
                <div className="video-info">
                  <div className="video-header">
                    <h3>{video.title || "Untitled Video"}</h3>
                  </div>

                  <p className="video-description">
                    {video.description || "No description available."}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <CommonPagination
        count={totalPages}
        page={page}
        onChange={handlePageChange}
        totalItems={totalVideos}
        currentCount={videos.length}
        itemLabel="Videos"
      />

      {selectedVideo && (
        <div className="video-modal-overlay" onClick={handleBack}>
          <div
            className="video-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="video-modal-header">
              <h2>{selectedVideo.title}</h2>
              <button className="close-modal-btn" onClick={handleBack}>
                <CloseIcon />
              </button>
            </div>

            <div className="video-modal-body custom-scrollbar">
              <div className="video-wrapper">
                {renderVideoPlayer(selectedVideo)}
              </div>
              <div className="video-details">
                <p>{selectedVideo.description}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Videos;
